// owner: jobs-domain agent — zod schemas + inferred types shared by api and web
import { z } from 'zod';
import { errors } from '../errors';
import { slugify } from '../helpers';
import {
  CentsSchema,
  CurrencySchema,
  EpochMsSchema,
  UlidSchema,
  paginated,
  PaginationQuerySchema,
} from './common';

export const JOB_STATUSES = ['draft', 'open', 'paused', 'closed', 'filled'] as const;
export const JobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof JobStatusSchema>;

export const RemoteModeSchema = z.enum(['onsite', 'hybrid', 'remote']);
export const EmploymentTypeSchema = z.enum([
  'full_time',
  'part_time',
  'contract',
  'temporary',
  'internship',
]);
export type EmploymentType = z.infer<typeof EmploymentTypeSchema>;
export const SalaryPeriodSchema = z.enum(['year', 'month', 'hour']);
export type SalaryPeriod = z.infer<typeof SalaryPeriodSchema>;

/** Lowercased, trimmed, de-duplicated skill list. */
export const SkillsSchema = z
  .array(z.string().trim().min(1).max(60))
  .max(50)
  .transform((xs) => [...new Set(xs.map((s) => s.toLowerCase()))]);

// ───────── status machine (pure) ─────────
const JOB_TRANSITIONS: Record<JobStatus, readonly JobStatus[]> = {
  draft: ['open', 'closed'],
  open: ['paused', 'closed', 'filled'],
  paused: ['open', 'closed'],
  closed: ['open'],
  filled: [],
};

export const allowedJobTransitions = (from: JobStatus): readonly JobStatus[] =>
  JOB_TRANSITIONS[from];
export const canTransitionJob = (from: JobStatus, to: JobStatus): boolean =>
  JOB_TRANSITIONS[from].includes(to);

export function assertJobTransition(from: JobStatus, to: JobStatus): void {
  if (canTransitionJob(from, to)) return;
  const next = JOB_TRANSITIONS[from];
  throw errors.conflict(
    `Job cannot move from '${from}' to '${to}'. ${
      next.length ? `Allowed next statuses: ${next.join(', ')}.` : `'${from}' is a final status.`
    }`,
    { from, to, allowed: next },
  );
}

export function jobSlug(title: string, suffix: string): string {
  const base = slugify(title, 80) || 'job';
  return `${base}-${suffix}`;
}

// ───────── request schemas ─────────
const jobFields = {
  title: z.string().trim().min(3).max(140),
  description: z.string().trim().min(10).max(20_000),
  location: z.string().trim().max(200).default(''),
  remote: RemoteModeSchema.default('onsite'),
  employmentType: EmploymentTypeSchema.default('full_time'),
  salaryMin: CentsSchema.nullish(),
  salaryMax: CentsSchema.nullish(),
  currency: CurrencySchema.default('USD'),
  salaryPeriod: SalaryPeriodSchema.default('year'),
  skills: SkillsSchema.default([]),
  seoEnabled: z.boolean().default(true),
  geoEnabled: z.boolean().default(true),
  closesAt: EpochMsSchema.nullish(),
};

const salaryOrder = (v: { salaryMin?: number | null; salaryMax?: number | null }) =>
  v.salaryMin == null || v.salaryMax == null || v.salaryMin <= v.salaryMax;
const salaryMsg = { message: 'salaryMin must be <= salaryMax', path: ['salaryMin'] };

export const JobCreateSchema = z
  .object({ orgId: UlidSchema, ...jobFields })
  .refine(salaryOrder, salaryMsg);
export type JobCreate = z.infer<typeof JobCreateSchema>;

export const JobUpdateSchema = z
  .object({
    title: jobFields.title,
    description: jobFields.description,
    location: z.string().trim().max(200),
    remote: RemoteModeSchema,
    employmentType: EmploymentTypeSchema,
    salaryMin: CentsSchema.nullable(),
    salaryMax: CentsSchema.nullable(),
    currency: CurrencySchema,
    salaryPeriod: SalaryPeriodSchema,
    skills: SkillsSchema,
    seoEnabled: z.boolean(),
    geoEnabled: z.boolean(),
    closesAt: EpochMsSchema.nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one field to update' })
  .refine(salaryOrder, salaryMsg);
export type JobUpdate = z.infer<typeof JobUpdateSchema>;

export const JobCloseSchema = z.object({ filled: z.boolean().default(false) });
export const JobIdParamSchema = z.object({ id: z.string().min(1).max(140) });

export const JobListQuerySchema = PaginationQuerySchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
  location: z.string().trim().min(1).max(200).optional(),
  remote: RemoteModeSchema.optional(),
  employmentType: EmploymentTypeSchema.optional(),
  /** Minimum salary in cents/yr; matches jobs whose top of range is >= this. Alias: salary_min. */
  salaryMin: z.coerce.number().int().nonnegative().optional(),
  salary_min: z.coerce.number().int().nonnegative().optional(),
  /** Org slug or id. */
  org: z.string().trim().min(1).max(140).optional(),
  /** Comma-separated; job must list ALL of them. */
  skills: z.string().trim().min(1).max(500).optional(),
});

export const JobManageQuerySchema = PaginationQuerySchema.extend({
  orgId: UlidSchema,
  status: JobStatusSchema.optional(),
});

// ───────── JobPosting-ready fields (schema.org) ─────────
const SCHEMA_ORG_EMPLOYMENT: Record<EmploymentType, string> = {
  full_time: 'FULL_TIME',
  part_time: 'PART_TIME',
  contract: 'CONTRACTOR',
  temporary: 'TEMPORARY',
  internship: 'INTERN',
};
const UNIT_TEXT: Record<SalaryPeriod, string> = { year: 'YEAR', month: 'MONTH', hour: 'HOUR' };

export const JobPostingFieldsSchema = z.object({
  title: z.string(),
  description: z.string(),
  employmentType: z.string(),
  jobLocationType: z.literal('TELECOMMUTE').nullable(),
  jobLocation: z.string().nullable(),
  datePosted: z.string().nullable(),
  validThrough: z.string().nullable(),
  baseSalary: z
    .object({
      currency: z.string(),
      minValue: z.number().nullable(),
      maxValue: z.number().nullable(),
      unitText: z.string(),
    })
    .nullable(),
});
export type JobPostingFields = z.infer<typeof JobPostingFieldsSchema>;

export interface JobPostingSource {
  title: string;
  description: string;
  employmentType: EmploymentType;
  remote: 'onsite' | 'hybrid' | 'remote';
  location: string;
  salaryMin: number | null;
  salaryMax: number | null;
  currency: string;
  salaryPeriod: SalaryPeriod;
  publishedAt: number | null;
  closesAt: number | null;
}

/** Only data the employer entered; baseSalary only when a salary exists (spec R-110). Money cents -> major units. */
export function toJobPostingFields(j: JobPostingSource): JobPostingFields {
  const hasSalary = j.salaryMin !== null || j.salaryMax !== null;
  return {
    title: j.title,
    description: j.description,
    employmentType: SCHEMA_ORG_EMPLOYMENT[j.employmentType],
    jobLocationType: j.remote === 'remote' ? 'TELECOMMUTE' : null,
    jobLocation: j.location ? j.location : null,
    datePosted: j.publishedAt === null ? null : new Date(j.publishedAt).toISOString().slice(0, 10),
    validThrough: j.closesAt === null ? null : new Date(j.closesAt).toISOString(),
    baseSalary: hasSalary
      ? {
          currency: j.currency,
          minValue: j.salaryMin === null ? null : j.salaryMin / 100,
          maxValue: j.salaryMax === null ? null : j.salaryMax / 100,
          unitText: UNIT_TEXT[j.salaryPeriod],
        }
      : null,
  };
}

// ───────── responses ─────────
export const JobSchema = z.object({
  id: UlidSchema,
  orgId: UlidSchema,
  org: z.object({ slug: z.string(), name: z.string(), logo: z.string().nullable() }),
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  location: z.string(),
  remote: RemoteModeSchema,
  employmentType: EmploymentTypeSchema,
  salaryMin: z.number().nullable(),
  salaryMax: z.number().nullable(),
  currency: z.string(),
  salaryPeriod: SalaryPeriodSchema,
  skills: z.array(z.string()),
  status: JobStatusSchema,
  seoEnabled: z.boolean(),
  geoEnabled: z.boolean(),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
  publishedAt: EpochMsSchema.nullable(),
  closesAt: EpochMsSchema.nullable(),
  /** null when seoEnabled=false: no structured-data fields are offered for an opted-out job. */
  posting: JobPostingFieldsSchema.nullable(),
});
export type Job = z.infer<typeof JobSchema>;

export const JobListSchema = paginated(JobSchema);
export const SavedJobsSchema = paginated(JobSchema.extend({ savedAt: EpochMsSchema }));
export const JobSavedSchema = z.object({ jobId: UlidSchema, saved: z.boolean() });
